import { useEffect, useState } from 'react';

import * as monthUtils from '@actual-app/core/shared/months';
import { q } from '@actual-app/core/shared/query';

import { aqlQuery } from '#queries/aqlQuery';

import type { Escopo } from './escopo';
import type { LinhaDoMes, ResumoDoMes } from './mesFinanceiro';
import { idsDeIrmasOrfas, resumirMes } from './mesFinanceiro';

type Retorno = {
  resumo: ResumoDoMes | null;
  carregando: boolean;
  erro: Error | null;
  /** Data da transação mais recente do mês — alimenta o "extrato até DD/MM". */
  ultimoDia: string | null;
  /**
   * As linhas CRUAS do mês, já normalizadas — expostas pra Task 10
   * (`Inicio.tsx` → `motor/useAgendadosDoMes.ts` → `motor/contasFixas.ts`,
   * `montarContasFixas`), que precisa cruzá-las com os agendados pra saber
   * quais contas fixas já foram pagas.
   *
   * ⚠️ Isso NÃO adiciona nenhuma consulta nova nem muda a sequência das
   * existentes — é só devolver, pra fora, um dado que o hook já buscava e
   * descartava depois de calcular `resumo`. Fica `[]` no estado de erro
   * (mesmo espírito de `resumo: null`: falha de leitura nunca deve parecer
   * "mês sem conta fixa nenhuma" — mas como não há `resumo` pra usar
   * `linhas` de qualquer jeito nesse caso, ver o guard
   * `dadosExibidos.resumo &&` em `Inicio.tsx`, o valor aqui é inofensivo).
   */
  linhas: LinhaDoMes[];
};

/**
 * Busca a conta (offbudget) das irmãs órfãs — pernas de transferência cuja
 * irmã não caiu no mesmo mês consultado (ver `LinhaDoMes.transfer_id` e
 * `Extras.contaDaIrmaOrfa` em `mesFinanceiro.ts` pro porquê disso acontecer).
 *
 * ⚠️ A consulta é ENXUTA DE PROPÓSITO — só `id` + `account.offbudget`, NUNCA
 * a transação inteira: trazer o resto (amount/date/notes) da irmã inflaria
 * `entrou`/`gastosVariaveis` deste mês com dado de OUTRO mês.
 *
 * Best-effort: se esta consulta falhar, o mês principal já carregou — não
 * faz sentido derrubar a tela inteira por causa de uma informação de apoio.
 * A falha vira "não sei", e `resumirMes` já sabe tratar isso como
 * `naoClassificado` (nunca como "transferência" adivinhada).
 */
async function buscarContaDaIrmaOrfa(
  idsOrfaos: string[],
): Promise<Record<string, boolean> | undefined> {
  if (idsOrfaos.length === 0) return undefined;

  try {
    const { data } = await aqlQuery(
      q('transactions')
        .filter({ id: { $oneof: idsOrfaos } })
        .select(['id', { contaForaDoOrcamento: 'account.offbudget' }]),
    );

    const irmas = (data ?? []) as Array<Record<string, unknown>>;
    const mapa: Record<string, boolean> = {};
    for (const irma of irmas) {
      mapa[String(irma.id)] = Boolean(irma.contaForaDoOrcamento);
    }
    return mapa;
  } catch {
    return undefined;
  }
}

/**
 * Liga o motor puro (`resumirMes`) numa consulta ao banco.
 *
 * A query é BURRA de propósito: traz o mês inteiro (sem filtrar por conta,
 * categoria ou tipo) e deixa toda a decisão pro motor, que é testável linha
 * a linha. ~350 linhas/mês no volume real.
 *
 * ⚠️ NÃO filtrar por conta/categoria aqui: uma transferência é UM evento
 * partido em DUAS linhas (uma por conta), e o motor precisa das DUAS pernas
 * pra saber se é remanejo puro, aporte (perna fora do orçamento) ou gasto de
 * outro escopo. Filtrar a consulta por conta derrubaria uma perna e o motor
 * perderia a informação de que aquilo é transferência — o número mentiria
 * calado, sem erro nenhum.
 *
 * O `select` com `{ contaForaDoOrcamento: 'account.offbudget' }` foi
 * verificado contra o banco real (browser mode, via `window.$query`/`$q`):
 * resolve a referência `account` corretamente por linha, inclusive nas duas
 * pernas de uma transferência simultânea (cada uma com o `offbudget` da
 * PRÓPRIA conta). Ver o relatório da Task 7 para o retorno bruto da query.
 *
 * ⚠️⚠️ Fix round 1 (achado ao vivo contra o app real): a correlação das duas
 * pernas de uma transferência (`canonizarTransferId`) mora dentro de
 * `resumirMes` agora — este hook só entrega o dado cru. A ÚNICA coisa extra
 * que o hook faz é: se sobrar alguma perna ÓRFÃ (a irmã caiu em outro mês —
 * ver `idsDeIrmasOrfas`), busca a conta da irmã numa SEGUNDA consulta enxuta
 * e passa isso como `extras.contaDaIrmaOrfa`. Sem essa segunda consulta, o
 * motor já é seguro por padrão (perna órfã vira `naoClassificado`, nunca
 * "transferência" adivinhada) — a consulta extra só existe pra reduzir
 * quanto cai nesse balde.
 */
export function useMesFinanceiro(mes: string, escopo: Escopo): Retorno {
  const [resumo, setResumo] = useState<ResumoDoMes | null>(null);
  const [linhas, setLinhas] = useState<LinhaDoMes[]>([]);
  const [ultimoDia, setUltimoDia] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<Error | null>(null);

  useEffect(() => {
    let cancelado = false;
    setCarregando(true);
    setErro(null);

    const inicio = monthUtils.firstDayOfMonth(mes);
    const fim = monthUtils.lastDayOfMonth(mes);

    const query = q('transactions')
      .filter({ date: { $gte: inicio }, $and: [{ date: { $lte: fim } }] })
      .select([
        'id',
        'date',
        'amount',
        'notes',
        'transfer_id',
        'schedule',
        'category',
        'is_parent',
        'is_child',
        'starting_balance_flag',
        { conta: 'account.id' },
        { contaForaDoOrcamento: 'account.offbudget' },
      ]);

    aqlQuery(query)
      .then(async ({ data }) => {
        if (cancelado) return;

        const brutas = (data ?? []) as Array<Record<string, unknown>>;

        const linhasDoMes: LinhaDoMes[] = brutas.map(t => ({
          id: String(t.id),
          date: String(t.date),
          amount: Number(t.amount ?? 0),
          notes: (t.notes as string | null) ?? null,
          transfer_id: (t.transfer_id as string | null) ?? null,
          schedule: (t.schedule as string | null) ?? null,
          category: (t.category as string | null) ?? null,
          contaForaDoOrcamento: Boolean(t.contaForaDoOrcamento),
          ehSaldoInicial: Boolean(t.starting_balance_flag),
          ehPai: Boolean(t.is_parent),
          ehFilho: Boolean(t.is_child),
        }));

        const idsOrfaos = idsDeIrmasOrfas(linhasDoMes);
        const contaDaIrmaOrfa = await buscarContaDaIrmaOrfa(idsOrfaos);

        if (cancelado) return;

        setResumo(resumirMes(linhasDoMes, escopo, { contaDaIrmaOrfa }));
        setLinhas(linhasDoMes);
        setUltimoDia(
          linhasDoMes.length > 0
            ? linhasDoMes.reduce((a, b) => (a.date > b.date ? a : b)).date
            : null,
        );
        setCarregando(false);
      })
      .catch((e: unknown) => {
        if (cancelado) return;
        // ⚠️ Falha de leitura NÃO pode virar "R$ 0,00" na tela. Zero num painel
        // financeiro lê-se como "não gastei nada", que é pior que um erro visível.
        setErro(e instanceof Error ? e : new Error(String(e)));
        setResumo(null);
        setLinhas([]);
        setCarregando(false);
      });

    return () => {
      cancelado = true;
    };
  }, [mes, escopo]);

  return { resumo, carregando, erro, ultimoDia, linhas };
}
